import { FC } from 'react'
import { SearchInput } from './SearchInput'
import { Actions } from './Actions'
import { useParams } from 'react-router-dom'
import { groupSlice, useGroup } from '@wellesley/group'
import HeaderWrapper from './HeaderWrapper'
import { cn } from '@wellesley/ui'
import { selectUploadFile } from '@wellesley/uploads'
import { useDispatch, useSelector } from 'react-redux'
import { ExtensionsMap, platformSettingsSlice } from '@wellesley/settings'
import useImageTextColor from 'hooks/useImageTextColor'
import { Group } from '@wellesley/openapi'
import { ChevronsDownUp, ChevronsUpDown } from 'lucide-react'
import HeaderBurger, { HeaderBurgerProps } from './HeaderBurger'

type GroupHeaderProps = HeaderBurgerProps & {
  group: Group
  extensions: ExtensionsMap
}

export const GroupHeader: FC<GroupHeaderProps> = ({
  group,
  extensions,
  onToggleSidebar,
}) => {
  const dispatch = useDispatch()

  const bannerUrl = group.banner
    ? selectUploadFile({
        extensions,
        upload: group.banner,
        size: window.innerWidth,
      })?.uri
    : null

  const textColorClass = useImageTextColor({
    imageUrl: bannerUrl,
  })

  const showBasicInfo = useSelector(groupSlice.selectors.showBasicInfo)

  return (
    <HeaderWrapper
      className={cn('relative', bannerUrl && 'bg-cover bg-center bg-no-repeat')}
      style={bannerUrl ? { backgroundImage: `url(${bannerUrl})` } : undefined}
    >
      {bannerUrl && (
        <div className="absolute inset-0 bg-slate-900_20 backdrop-blur-[12.5px]" />
      )}
      <div className="relative z-10 flex items-center gap-2">
        <div
          className={cn(
            'flex select-none items-center  text-[24px] font-semibold capitalize',
            textColorClass,
          )}
        >
          <HeaderBurger onToggleSidebar={onToggleSidebar} className="mr-s" />
          {group.displayName}
          <div className="px-xxs">
            {!showBasicInfo ? (
              <ChevronsUpDown
                data-testid="chevrons-up-down"
                className="cursor-pointer"
                onClick={() =>
                  dispatch(groupSlice.actions.setShowBasicInfo(true))
                }
              />
            ) : (
              <ChevronsDownUp
                data-testid="chevrons-down-up"
                className="cursor-pointer"
                onClick={() =>
                  dispatch(groupSlice.actions.setShowBasicInfo(false))
                }
              />
            )}
          </div>
        </div>
      </div>
      <SearchInput />
      <Actions className={textColorClass} />
    </HeaderWrapper>
  )
}

const CmpWrapper: FC<HeaderBurgerProps> = ({ onToggleSidebar }) => {
  const { groupname = '' } = useParams()

  const { group, isLoading } = useGroup({ groupname })

  const extensions = useSelector(
    platformSettingsSlice.selectors.selectSupportedExtensions,
  )

  if (isLoading || !group) {
    return null
  }

  return (
    <GroupHeader
      group={group}
      extensions={extensions}
      onToggleSidebar={onToggleSidebar}
    />
  )
}

export default CmpWrapper
